import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ProgressBar } from 'primereact/progressbar';
import { Tooltip } from 'primereact/tooltip';
import { useSelector, useDispatch } from 'react-redux';
import { loadQuestionPerformanceAsync, selectQuestionPerformance } from '../dashboardSlice';
import { ChartCard } from './ChartCard';
import { MemoChart } from './MemoChart';
import { ModuleService } from '../../../service/ModuleService';
import { TestService } from '../../../service/TestService';
import { MedicalServiceService } from '../../../service/MedicalServiceService';
import { DashboardService } from '../../../service/DashboardService';
import { useThemeVersion, getStoredColorMode } from '../../../useTheme';
import { categoryColor } from '../categoryColors';
import QuestionImage from '../../../components/QuestionImage';

const moduleService = new ModuleService();
const testService = new TestService();
const medicalServiceService = new MedicalServiceService();
const dashboardService = new DashboardService();

const truncate = (text, max = 40) => {
    if (!text) return '';
    return text.length > max ? `${text.substring(0, max)}...` : text;
};

const buildChartData = (items, field, label, color) => ({
    labels: items.map(item => truncate(item.questionTitle)),
    datasets: [{
        label,
        data: items.map(item => item[field]),
        backgroundColor: color
    }]
});

// Diferente de ModulePerformanceChart/TestPerformanceChart (cada um sempre
// num contexto fixo), aqui o Quiz mistura questões de módulo e de prova na
// mesma lista — só tem uma identidade de cor quando o filtro Módulo ou Prova
// está ativo; sem filtro nenhum (questões mistas), fica neutro.
const quizCategoryColor = (filters, colorMode) => {
    if (filters?.testId) return categoryColor('test', colorMode);
    if (filters?.moduleId) return categoryColor('module', colorMode);
    return '#9E9E9E';
};

const dropdownItemTemplate = (option) => (
    <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{option.label}</span>
);

const LIMIT_OPTIONS = [100, 200, 300, 400, 500].map(value => ({ label: `${value} questões`, value }));

export const QuestionPerformanceChart = () => {
    const dispatch = useDispatch();
    const { data, status, error, filters } = useSelector(selectQuestionPerformance);
    const [moduleOptions, setModuleOptions] = useState([]);
    const [testOptions, setTestOptions] = useState([]);
    const [allServices, setAllServices] = useState([]);
    const [questionOptions, setQuestionOptions] = useState([]);
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    // getComputedStyle abaixo (em buildChartOptions) só lê o CSS de novo se
    // ela recalcular — sem isso, trocar de tema depois de montado deixa a
    // cor antiga presa.
    const themeVersion = useThemeVersion();
    // Ordenação independente por gráfico — inverter uma não afeta a outra.
    const [quizSortAsc, setQuizSortAsc] = useState(true);
    const [practiceTestSortAsc, setPracticeTestSortAsc] = useState(true);
    const loading = status === 'loading';

    useEffect(() => {
        moduleService.getModules(0, 0, 500, 'description', 1)
            .then(res => setModuleOptions((res.data || []).map(m => ({ label: m.description, value: m.id }))))
            .catch(() => setModuleOptions([]));

        // Só provas com pelo menos 1 questão objetiva — uma prova 100% oral
        // nunca teria dado nenhum de Quiz/Simulado pra mostrar.
        dashboardService.getTestsForFilter()
            .then(res => setTestOptions((res || []).map(t => ({
                label: t.year ? `${t.title} (${t.year})` : t.title,
                value: t.id
            }))))
            .catch(() => setTestOptions([]));

        medicalServiceService.getMedicalServices(0, 0, 500, 'name', 1)
            .then(res => setAllServices(res.data || []))
            .catch(() => setAllServices([]));
    }, []);

    // UF condiciona o serviço (uma UF só mostra serviços daquela UF) — a
    // lista completa fica em allServices, filtrada aqui pela UF ativa.
    const serviceOptions = useMemo(() => {
        const filtered = filters?.uf
            ? allServices.filter(s => s.state === filters.uf)
            : allServices;
        return filtered.map(s => ({ label: `${s.name} (${s.state})`, value: s.id }));
    }, [allServices, filters?.uf]);

    const ufOptions = useMemo(() => {
        const states = Array.from(new Set(allServices.map(s => s.state).filter(Boolean))).sort();
        return states.map(state => ({ label: state, value: state }));
    }, [allServices]);

    // A lista de questões só faz sentido condicionada por módulo e/ou prova
    // (senão seriam milhares de opções) — refaz a busca sempre que um dos
    // dois muda, combinando ambos quando estiverem preenchidos.
    useEffect(() => {
        const moduleId = filters?.moduleId;
        const testId = filters?.testId;
        if (!moduleId && !testId) {
            setQuestionOptions([]);
            return;
        }
        testService.getQuestionsForFilter({ moduleId, testId })
            .then(res => setQuestionOptions((res || []).map(q => ({
                label: q.title || truncate(q.description, 80) || `Questão ${q.id}`,
                value: q.id
            }))))
            .catch(() => setQuestionOptions([]));
    }, [filters?.moduleId, filters?.testId]);

    const applyFilters = (newFilters) => {
        dispatch(loadQuestionPerformanceAsync({ ...filters, ...newFilters }));
    };

    // Mesmos filtros ativos no gráfico (período/serviço/UF/contexto
    // módulo-ou-prova), pra que o % de cada alternativa bata com o % que a
    // barra já mostrava.
    const openQuestionDetail = async (questionId) => {
        setDetailVisible(true);
        setDetailLoading(true);
        setDetailData(null);
        try {
            const detail = await dashboardService.getQuestionDetail(questionId, {
                moduleId: filters?.moduleId,
                testId: filters?.testId,
                from: filters?.from,
                to: filters?.to,
                serviceId: filters?.serviceId,
                uf: filters?.uf,
            });
            setDetailData(detail);
        } catch (e) {
            setDetailData(null);
        } finally {
            setDetailLoading(false);
        }
    };

    // Adia pro próximo tick: o wrapper Chart do PrimeReact reconstrói a
    // instância do Chart.js a cada render (useEffect sem deps), então
    // disparar o setState aqui dentro, ainda no meio do próprio evento de
    // clique do Chart.js, destrói o chart no meio do seu próprio processamento
    // interno do evento — daí o "controller is null" no tooltip.
    const buildBarClickHandler = (items) => (event, elements) => {
        if (!elements || !elements.length) return;
        const item = items[elements[0].index];
        if (item) setTimeout(() => openQuestionDetail(item.questionId), 0);
    };

    // Quiz e simulado têm naturezas estatísticas diferentes (quiz nunca
    // acumula mais de uma resposta por usuário/contexto; simulado acumula
    // uma por tentativa), então cada gráfico é ordenado pela sua própria
    // métrica — questões sem nenhuma resposta daquela origem ficam de fora
    // desse gráfico específico, em vez de aparecer como zero.
    const quizItems = useMemo(() => {
        if (!Array.isArray(data)) return [];
        const sign = quizSortAsc ? 1 : -1;
        return data
            .filter(item => item.quizCorrectPercentage !== null)
            .sort((a, b) => sign * (a.quizCorrectPercentage - b.quizCorrectPercentage));
    }, [data, quizSortAsc]);

    const practiceTestItems = useMemo(() => {
        if (!Array.isArray(data)) return [];
        const sign = practiceTestSortAsc ? 1 : -1;
        return data
            .filter(item => item.practiceTestCorrectPercentage !== null)
            .sort((a, b) => sign * (a.practiceTestCorrectPercentage - b.practiceTestCorrectPercentage));
    }, [data, practiceTestSortAsc]);

    const quizChartData = useMemo(
        () => buildChartData(quizItems, 'quizCorrectPercentage', 'Quiz — % de Acerto', quizCategoryColor(filters, getStoredColorMode())),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [quizItems, filters?.moduleId, filters?.testId, themeVersion]
    );

    const practiceTestChartData = useMemo(
        () => buildChartData(practiceTestItems, 'practiceTestCorrectPercentage', 'Simulado — % de Acerto', categoryColor('practiceTest', getStoredColorMode())),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [practiceTestItems, themeVersion]
    );

    // O tooltip mostra o título completo da questão (não o truncado usado no
    // eixo Y, que precisa ser curto pra caber) — busca no array original pelo
    // índice da barra.
    const buildChartOptions = useCallback((items) => ({
        indexAxis: 'y',
        onClick: buildBarClickHandler(items),
        onHover: (event, elements) => {
            event.native.target.style.cursor = elements.length ? 'pointer' : 'default';
        },
        scales: {
            x: {
                min: 0,
                max: 100,
                ticks: {
                    color: getComputedStyle(document.documentElement).getPropertyValue('--text-color')
                }
            },
            y: {
                ticks: {
                    color: getComputedStyle(document.documentElement).getPropertyValue('--text-color')
                }
            }
        },
        plugins: {
            legend: false,
            tooltip: {
                callbacks: {
                    title: (tooltipItems) => {
                        const index = tooltipItems[0]?.dataIndex;
                        return index != null ? (items[index]?.questionTitle || '') : '';
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [filters, themeVersion]);

    const quizChartOptions = useMemo(() => buildChartOptions(quizItems), [quizItems, buildChartOptions]);
    const practiceTestChartOptions = useMemo(
        () => buildChartOptions(practiceTestItems),
        [practiceTestItems, buildChartOptions]
    );

    return (
        <>
            <div className="flex flex-wrap gap-3 mb-3">
                <div className="flex flex-column gap-1">
                    <label htmlFor="question-perf-module" className="text-xs text-color-secondary">Módulo</label>
                    <Dropdown
                        inputId="question-perf-module"
                        value={filters?.moduleId ?? null}
                        options={moduleOptions}
                        onChange={(e) => applyFilters({ moduleId: e.value, questionId: null })}
                        placeholder="Filtrar por módulo"
                        showClear
                        filter
                        className="w-full md:w-15rem"
                        panelStyle={{ width: '20rem' }}
                        itemTemplate={dropdownItemTemplate}
                    />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="question-perf-test" className="text-xs text-color-secondary">Prova</label>
                    <Dropdown
                        inputId="question-perf-test"
                        value={filters?.testId ?? null}
                        options={testOptions}
                        onChange={(e) => applyFilters({ testId: e.value, questionId: null })}
                        placeholder="Filtrar por prova"
                        showClear
                        filter
                        className="w-full md:w-15rem"
                        panelStyle={{ width: '20rem' }}
                        itemTemplate={dropdownItemTemplate}
                    />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="question-perf-question" className="text-xs text-color-secondary">Questão</label>
                    <Dropdown
                        inputId="question-perf-question"
                        value={filters?.questionId ?? null}
                        options={questionOptions}
                        onChange={(e) => applyFilters({ questionId: e.value })}
                        placeholder={questionOptions.length ? 'Filtrar por questão' : 'Escolha módulo/prova primeiro'}
                        disabled={!questionOptions.length}
                        showClear
                        filter
                        className="w-full md:w-15rem"
                        panelStyle={{ width: '20rem' }}
                        itemTemplate={dropdownItemTemplate}
                    />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="question-perf-uf" className="text-xs text-color-secondary">UF</label>
                    <Dropdown
                        inputId="question-perf-uf"
                        value={filters?.uf ?? null}
                        options={ufOptions}
                        onChange={(e) => applyFilters({ uf: e.value, serviceId: null })}
                        placeholder="Filtrar por UF"
                        showClear
                        className="w-full md:w-15rem"
                    />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="question-perf-service" className="text-xs text-color-secondary">Serviço</label>
                    <Dropdown
                        inputId="question-perf-service"
                        value={filters?.serviceId ?? null}
                        options={serviceOptions}
                        onChange={(e) => applyFilters({ serviceId: e.value })}
                        placeholder="Filtrar por serviço"
                        showClear
                        filter
                        className="w-full md:w-15rem"
                        panelStyle={{ width: '20rem' }}
                        itemTemplate={dropdownItemTemplate}
                    />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="question-perf-limit" className="text-xs text-color-secondary">Quantidade</label>
                    <Dropdown
                        inputId="question-perf-limit"
                        value={filters?.limit ?? 100}
                        options={LIMIT_OPTIONS}
                        onChange={(e) => applyFilters({ limit: e.value })}
                        className="w-full md:w-15rem"
                    />
                </div>
                <div className="flex align-items-center gap-2 align-self-end pb-2">
                    <InputSwitch
                        inputId="question-perf-active-only"
                        checked={!!filters?.activeOnly}
                        onChange={(e) => applyFilters({ activeOnly: e.value })}
                        disabled={loading}
                    />
                    <label htmlFor="question-perf-active-only" className="text-xs text-color-secondary">Somente ativos</label>
                </div>
            </div>
            <ChartCard
                title={`Quiz — Desempenho por Questão (${quizSortAsc ? 'piores' : 'melhores'} primeiro)`}
                loading={loading}
                error={error}
                onReload={() => applyFilters({})}
                headerExtra={
                    <Button
                        icon={quizSortAsc ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up'}
                        rounded
                        text
                        tooltip={quizSortAsc ? 'Mostrando piores primeiro' : 'Mostrando melhores primeiro'}
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => setQuizSortAsc(v => !v)}
                    />
                }
            >
                <MemoChart data={quizChartData} options={quizChartOptions} />
            </ChartCard>
            <div className="mt-3">
                <ChartCard
                    title={`Simulado — Desempenho por Questão (${practiceTestSortAsc ? 'piores' : 'melhores'} primeiro)`}
                    loading={loading}
                    error={error}
                    onReload={() => applyFilters({})}
                    headerExtra={
                        <Button
                            icon={practiceTestSortAsc ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up'}
                            rounded
                            text
                            tooltip={practiceTestSortAsc ? 'Mostrando piores primeiro' : 'Mostrando melhores primeiro'}
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => setPracticeTestSortAsc(v => !v)}
                        />
                    }
                >
                    <MemoChart data={practiceTestChartData} options={practiceTestChartOptions} />
                </ChartCard>
            </div>
            <Dialog
                header="Detalhe da Questão"
                visible={detailVisible}
                onHide={() => setDetailVisible(false)}
                style={{ width: '40rem', maxWidth: '95vw' }}
            >
                {detailLoading ? (
                    <div className="flex justify-content-center p-5">
                        <ProgressSpinner />
                    </div>
                ) : !detailData ? (
                    <p>Não foi possível carregar a questão.</p>
                ) : (
                    <div>
                        <Tooltip target=".performance-progressbar" />
                        <div className="text-center mb-3">
                            <QuestionImage questionId={detailData.questionId} type="main" width={200} />
                        </div>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{detailData.description}</p>
                        <div className="mt-4">
                            {detailData.choices.map(choice => (
                                <div
                                    key={choice.choiceId}
                                    className="mb-3 p-2 border-round"
                                    style={{
                                        // Cor fixa (não var(--green-*), que no dark theme mantém o
                                        // mesmo verde bem claro do light theme mas o texto herda a
                                        // cor clara do tema, ficando ilegível) — mesmo par
                                        // fundo/texto já usado nos badges "Ativo" (product-badge
                                        // status-instock), que funciona em qualquer tema.
                                        border: choice.isCorrect ? '2px solid #256029' : '1px solid var(--surface-300)',
                                        background: choice.isCorrect ? '#C8E6C9' : 'transparent',
                                        color: choice.isCorrect ? '#256029' : undefined
                                    }}
                                >
                                    <div className="flex align-items-center gap-2 mb-2">
                                        <strong>{choice.option}</strong>
                                        <span style={{ whiteSpace: 'pre-wrap' }}>{choice.description}</span>
                                        {choice.isCorrect && (
                                            <i className="pi pi-check-circle text-green-600" title="Correta" />
                                        )}
                                    </div>
                                    <div className="flex flex-column gap-2">
                                        <div className="flex align-items-center gap-2">
                                            <span className="text-xs" style={{ width: '5rem' }}>Quiz</span>
                                            <ProgressBar
                                                value={choice.quizPercentage ?? 0}
                                                showValue
                                                color={quizCategoryColor(filters, getStoredColorMode())}
                                                className="performance-progressbar"
                                                style={{ flex: 1, height: '1rem' }}
                                                data-pr-tooltip={`Quiz: ${choice.quizPercentage ?? 0}% de escolha`}
                                                data-pr-position="top"
                                            />
                                        </div>
                                        <div className="flex align-items-center gap-2">
                                            <span className="text-xs" style={{ width: '5rem' }}>Simulado</span>
                                            <ProgressBar
                                                value={choice.practiceTestPercentage ?? 0}
                                                showValue
                                                color={categoryColor('practiceTest', getStoredColorMode())}
                                                className="performance-progressbar"
                                                style={{ flex: 1, height: '1rem' }}
                                                data-pr-tooltip={`Simulado: ${choice.practiceTestPercentage ?? 0}% de escolha`}
                                                data-pr-position="top"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Dialog>
            <style>{`
                /* O tema deixa o texto do label cinza (#495057) sobre o
                   preenchimento azul escuro (#112540) por padrão — contraste
                   ruim. Aqui força branco só nessas barras. */
                .performance-progressbar .p-progressbar-label {
                    color: #ffffff;
                    font-weight: 600;
                }
            `}</style>
        </>
    );
};
